import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
} from '@chakra-ui/react';
import {
  MdAutoAwesome,
  MdContentCopy,
  MdNotificationsActive,
} from 'react-icons/md';
import { OutlineButton, PrimaryButton, StatusBadge } from '../../../../components/ui';
import {
  aiVerifyUserEquipment,
  getAiVerifyHistoryUserEquipment,
  requestOwnerAadhaarUserEquipment,
} from '../../../../repositories/userEquipment.repository';
import { sendAppUserNotification } from '../../../../repositories/appUser.repository';
import { formatDateTimeDisplay } from '../../../../utils/personName';

function checkpointColor(status) {
  if (status === 'pass') return 'green';
  if (status === 'fail') return 'red';
  if (status === 'warn') return 'orange';
  return 'gray';
}

function overallBadge(status) {
  if (status === 'ok') return { label: 'OK', status: 'verified' };
  if (status === 'not_ok') return { label: 'Not OK', status: 'error' };
  return { label: 'Needs review', status: 'pending' };
}

function scoreColor(percent) {
  if (percent >= 85) return 'green';
  if (percent >= 70) return 'teal';
  if (percent >= 50) return 'orange';
  return 'red';
}

function docStatusColor(status) {
  if (status === 'pass') return 'green';
  if (status === 'fail' || status === 'missing') return 'red';
  if (status === 'warn') return 'orange';
  return 'gray';
}

/** Soften harsh AI wording for member-facing copy. */
function softenMemberText(text) {
  return String(text || '')
    .replace(/\bKYC failed\b/gi, 'A few details need an update')
    .replace(/\bfailed due to\b/gi, 'needs a quick update for')
    .replace(/\bfraud signals?\b/gi, 'document review')
    .replace(/\bfraud\b/gi, 'document issue')
    .replace(/\brejected\b/gi, 'needs another look')
    .replace(/\binvalid\b/gi, 'does not match')
    .replace(/\bstock photos?\b/gi, 'photos that do not look like your vehicle')
    .replace(/\ban unrelated\b/gi, 'a mismatched')
    .replace(/\bunrelated\b/gi, 'mismatched')
    .replace(/\bmismatch(es)?\b/gi, 'difference$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function shortDetail(text, max = 100) {
  const t = softenMemberText(text);
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const breakAt = Math.max(
    cut.lastIndexOf('. '),
    cut.lastIndexOf('; '),
    cut.lastIndexOf(', '),
    cut.lastIndexOf(' '),
  );
  const base = (breakAt > 40 ? cut.slice(0, breakAt) : cut).trimEnd();
  return `${base.replace(/[,;.\s]+$/, '')}.`;
}

/**
 * Fit to notification body limit (1000 chars). Never cut mid-sentence —
 * drop whole lines, always keep a complete closing.
 */
function fitNotificationBody(text, max = 1000) {
  const closing =
    'Once you update the documents or photos in the app, our team will happily review again. Thank you.';
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (raw.length <= max) return raw;

  let head = raw;
  if (head.endsWith(closing)) {
    head = head.slice(0, -closing.length).trimEnd();
  }

  const budget = Math.max(120, max - closing.length - 2);
  const lines = head.split('\n');
  const kept = [];
  for (const line of lines) {
    const next = kept.length ? `${kept.join('\n')}\n${line}` : line;
    if (next.length > budget) break;
    kept.push(line);
  }
  while (
    kept.length &&
    (!kept[kept.length - 1].trim() || /:$/.test(kept[kept.length - 1].trim()))
  ) {
    kept.pop();
  }
  if (!kept.length) {
    const intro = head.split('\n')[0] || 'A few items need a small update for verification.';
    const trimmedIntro =
      intro.length > budget
        ? `${intro.slice(0, budget - 1).replace(/\s+\S*$/, '').trimEnd()}.`
        : intro;
    return `${trimmedIntro}\n\n${closing}`.slice(0, max);
  }
  return `${kept.join('\n').trim()}\n\n${closing}`.slice(0, max);
}

/** Support new AI documentScores and legacy upload-point scoreBreakdown. */
function documentScoreRows(result) {
  if (Array.isArray(result?.documentScores) && result.documentScores.length) {
    return result.documentScores.map((row) => ({
      id: row.id || row.docType,
      label: row.label || row.id,
      scorePercent:
        typeof row.scorePercent === 'number' ? row.scorePercent : null,
      status: row.status || 'warn',
      detail: row.detail || '',
      legacy:
        row.earned != null && row.max != null
          ? `${row.earned}/${row.max}`
          : null,
    }));
  }
  if (Array.isArray(result?.scoreBreakdown) && result.scoreBreakdown.length) {
    return result.scoreBreakdown.map((row) => ({
      id: row.id,
      label: row.label || row.id,
      scorePercent:
        row.max > 0
          ? Math.round((Number(row.earned) / Number(row.max)) * 100)
          : 0,
      status: 'warn',
      detail: row.detail || '',
      legacy: `${row.earned}/${row.max}`,
    }));
  }
  return [];
}

/**
 * User-facing message for copy / in-app notification.
 * Built in admin UI from AI verify fields (mismatches, docs, alerts) —
 * Gemini does not write this text directly. Cap: 1000 characters (DB limit).
 */
export function buildAiEquipmentUserMessage(result, asset) {
  if (!result) return '';
  const lines = [];
  const vehicle =
    [asset?.regNo, asset?.equipmentType || asset?.typeName]
      .filter(Boolean)
      .join(' · ') || 'your equipment';
  const closing =
    'Once you update the documents or photos in the app, our team will happily review again. Thank you.';

  if (result.overallStatus === 'ok') {
    lines.push(
      `Thank you — documents for ${vehicle} look consistent. Our team will complete equipment verification shortly.`,
    );
  } else {
    lines.push(
      `Thanks for uploading documents for ${vehicle}. A few items need a small update so we can continue verification smoothly.`,
    );
  }

  const missing = Array.isArray(result.missingRequired)
    ? result.missingRequired.filter(Boolean)
    : [];
  if (missing.length) {
    lines.push('');
    lines.push('Please upload these compulsory documents:');
    missing.forEach((m, i) => lines.push(`${i + 1}. ${softenMemberText(m)}`));
  }

  const mismatches = Array.isArray(result.mismatches)
    ? result.mismatches.map((m) => shortDetail(m, 120)).filter(Boolean)
    : [];

  // Prefer mismatches; otherwise use weak document grades (avoid duplicating both).
  if (mismatches.length) {
    lines.push('');
    lines.push('Kindly update these:');
    mismatches.slice(0, 8).forEach((m, i) => lines.push(`${i + 1}. ${m}`));
  } else {
    const weakDocs = documentScoreRows(result).filter(
      (d) =>
        d.status === 'fail' ||
        d.status === 'missing' ||
        d.status === 'warn' ||
        (typeof d.scorePercent === 'number' && d.scorePercent < 60),
    );
    if (weakDocs.length) {
      lines.push('');
      lines.push('Kindly update these:');
      weakDocs.slice(0, 8).forEach((d, i) => {
        const detail = shortDetail(d.detail || '', 100);
        lines.push(`${i + 1}. ${d.label}${detail ? `: ${detail}` : ''}`);
      });
    }
  }

  const alerts = Array.isArray(result.complianceAlerts)
    ? result.complianceAlerts.filter(Boolean)
    : [];
  if (alerts.length) {
    lines.push('');
    lines.push('Compliance reminders:');
    alerts.slice(0, 5).forEach((a) => lines.push(`• ${shortDetail(a, 80)}`));
  }

  lines.push('');
  lines.push(closing);

  return fitNotificationBody(lines.join('\n').trim(), 1000);
}

function historyStatusBadge(entry) {
  if (entry?.status === 'error') {
    return { label: 'Error', colorScheme: 'red' };
  }
  if (entry?.overallStatus === 'ok') {
    return { label: 'OK', colorScheme: 'green' };
  }
  if (entry?.overallStatus === 'not_ok') {
    return { label: 'Not OK', colorScheme: 'red' };
  }
  return { label: 'Needs review', colorScheme: 'orange' };
}

export function EquipmentAiVerifyModal({
  isOpen,
  onClose,
  asset,
  onOwnerAadhaarRequested,
}) {
  const toast = useToast();
  const bodyRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [requestingAadhaar, setRequestingAadhaar] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(false);

  const userMessage = useMemo(
    () => buildAiEquipmentUserMessage(result, asset),
    [result, asset],
  );
  const notifyTitle =
    result?.overallStatus === 'ok'
      ? 'Equipment document review update'
      : 'A quick update needed for your equipment';
  const ownerUserId = asset?.userId;

  const scrollBodyToTop = () => {
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (el && typeof el.scrollTo === 'function') {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  const refreshHistory = async (equipmentId) => {
    if (!equipmentId) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const data = await getAiVerifyHistoryUserEquipment(equipmentId);
      setHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setHistory([]);
      setHistoryError(e?.message || 'Failed to load previous runs');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !asset?.id) return;
    refreshHistory(asset.id);
  }, [isOpen, asset?.id]);

  const run = async () => {
    if (!asset?.id) return;
    setLoading(true);
    setError('');
    setResult(null);
    setSelectedHistoryId(null);
    setViewingHistory(false);
    try {
      const data = await aiVerifyUserEquipment(asset.id);
      setResult(data);
      await refreshHistory(asset.id);
      scrollBodyToTop();
    } catch (e) {
      setError(e?.message || 'AI verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const openHistoryEntry = (entry) => {
    if (!entry) return;
    setSelectedHistoryId(entry.id);
    setViewingHistory(true);
    setError('');

    // Prefer nested snapshot; fall back to top-level fields if needed.
    const nested =
      entry.result && typeof entry.result === 'object' ? entry.result : null;
    const snapshot = nested ||
      (entry.overallStatus != null ||
      entry.summary != null ||
      entry.scorePercent != null
        ? {
            overallStatus: entry.overallStatus ?? null,
            summary: entry.summary ?? null,
            scorePercent: entry.scorePercent ?? null,
            scoreLabel: entry.scoreLabel ?? null,
            documentScores: Array.isArray(entry.documentScores)
              ? entry.documentScores
              : [],
            checkpoints: Array.isArray(entry.checkpoints)
              ? entry.checkpoints
              : [],
            mismatches: Array.isArray(entry.mismatches) ? entry.mismatches : [],
            missingRequired: Array.isArray(entry.missingRequired)
              ? entry.missingRequired
              : [],
            complianceAlerts: Array.isArray(entry.complianceAlerts)
              ? entry.complianceAlerts
              : [],
            docsUsed: Array.isArray(entry.docsUsed) ? entry.docsUsed : [],
          }
        : null);

    if (entry.status === 'error' && !snapshot) {
      setResult(null);
      setError(
        entry.errorMessage || 'This run did not produce a usable result.',
      );
      scrollBodyToTop();
      return;
    }
    if (!snapshot) {
      setResult(null);
      setError(
        entry.errorMessage || 'This run did not produce a usable result.',
      );
      scrollBodyToTop();
      return;
    }
    setResult(snapshot);
    scrollBodyToTop();
  };

  const handleClose = () => {
    setError('');
    setResult(null);
    setHistory([]);
    setHistoryError('');
    setSelectedHistoryId(null);
    setViewingHistory(false);
    setLoading(false);
    setSending(false);
    setHistoryLoading(false);
    onClose();
  };

  const copyMessage = async () => {
    if (!userMessage) return;
    try {
      await navigator.clipboard.writeText(userMessage);
      toast({
        title: 'Message copied',
        status: 'success',
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Could not copy',
        status: 'error',
        position: 'top-right',
      });
    }
  };

  const sendAsNotification = async () => {
    if (!ownerUserId || !userMessage) return;
    setSending(true);
    try {
      await sendAppUserNotification(ownerUserId, {
        title: notifyTitle,
        body: userMessage,
      });
      toast({
        title: 'Notification sent',
        description: 'Owner will see this in their Notifications tab.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to send notification',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSending(false);
    }
  };

  const requestOwnerAadhaar = async () => {
    if (!asset?.id || requestingAadhaar) return;
    setRequestingAadhaar(true);
    try {
      const ownership = await requestOwnerAadhaarUserEquipment(
        asset.id,
        result?.suggestOwnerAadhaarReason ||
          'RC name does not match the registered member. Please upload the vehicle owner’s Aadhaar on this equipment.',
      );
      onOwnerAadhaarRequested?.(ownership);
      toast({
        title: 'Owner Aadhaar requested',
        description: 'Member notified to upload on this equipment.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to request owner Aadhaar',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setRequestingAadhaar(false);
    }
  };

  const overall = overallBadge(result?.overallStatus);
  const selectedHistory = history.find((h) => h.id === selectedHistoryId);
  const scorePercent = Number(result?.scorePercent);
  const hasScore = Number.isFinite(scorePercent);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={2}>
            <MdAutoAwesome />
            Verify equipment by AI
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody ref={bodyRef}>
          <Text fontSize="13px" color="text.secondary" mb={3}>
            Does not change status. AI scores every uploaded equipment document
            and photo for correctness and quality (0–100%), not mere presence.
            Compulsory papers: RC copy, PUC, Insurance. After verify, copy or send
            a clear message to the owner — same flow as member AI verify.
          </Text>

          {viewingHistory && selectedHistory ? (
            <Box
              mb={3}
              px={3}
              py={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.input"
            >
              <Text fontSize="12px" color="text.secondary">
                Viewing past run:{' '}
                <Text as="span" fontWeight="600" color="text.primary">
                  {formatDateTimeDisplay(selectedHistory.ranAt)}
                </Text>
              </Text>
            </Box>
          ) : null}

          {!result && !loading && !error ? (
            <Box
              p={4}
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              bg="bg.input"
              mb={4}
            >
              <Text fontSize="14px" mb={3}>
                AI reviews every uploaded equipment document and photo for quality
                and correctness (not just presence), then returns a 0–100% score
                with a grade per file.
              </Text>
              <PrimaryButton size="sm" leftIcon={<MdAutoAwesome />} onClick={run}>
                Run AI verify
              </PrimaryButton>
            </Box>
          ) : null}

          {loading ? (
            <Flex align="center" gap={3} py={8} justify="center">
              <Spinner />
              <Text fontSize="14px">Running AI verification…</Text>
            </Flex>
          ) : null}

          {error ? (
            <Box
              p={3}
              borderRadius="md"
              bg="red.50"
              borderWidth="1px"
              borderColor="red.200"
              mb={3}
            >
              <Text fontSize="14px" color="red.700" fontWeight="600">
                {error}
              </Text>
              {!viewingHistory ? (
                <OutlineButton size="sm" mt={3} onClick={run}>
                  Try again
                </OutlineButton>
              ) : null}
            </Box>
          ) : null}

          {result ? (
            <VStack align="stretch" spacing={4} mb={4}>
              <Flex align="center" gap={3} wrap="wrap">
                <StatusBadge {...overall}>{overall.label}</StatusBadge>
                {hasScore ? (
                  <Badge
                    colorScheme={scoreColor(scorePercent)}
                    fontSize="14px"
                    px={3}
                    py={1}
                    borderRadius="md"
                  >
                    {scorePercent}% · {result.scoreLabel || 'Score'}
                  </Badge>
                ) : null}
                <Text fontSize="13px" color="text.secondary" flex="1" minW="200px">
                  {result.summary}
                </Text>
              </Flex>

              {result.suggestOwnerAadhaar && !viewingHistory ? (
                <Box
                  p={3}
                  borderWidth="1px"
                  borderColor="utkarsh.amber"
                  borderRadius="md"
                  bg="bg.amberTint"
                >
                  <Text fontSize="14px" fontWeight="700" mb={1}>
                    Owner Aadhaar may be needed
                  </Text>
                  <Text fontSize="13px" color="text.secondary" mb={3}>
                    {result.suggestOwnerAadhaarReason ||
                      'RC name does not match the registered member. Request the vehicle owner’s Aadhaar — the member uploads it on this equipment only.'}
                  </Text>
                  <PrimaryButton
                    size="sm"
                    onClick={requestOwnerAadhaar}
                    isLoading={requestingAadhaar}
                    isDisabled={
                      asset?.ownershipStatus === 'owner_aadhaar_requested' ||
                      asset?.ownershipStatus === 'owner_aadhaar_uploaded'
                    }
                  >
                    {asset?.ownershipStatus === 'owner_aadhaar_requested' ||
                    asset?.ownershipStatus === 'owner_aadhaar_uploaded'
                      ? 'Owner Aadhaar already requested'
                      : 'Request owner Aadhaar'}
                  </PrimaryButton>
                </Box>
              ) : null}

              {hasScore ? (
                <Box
                  p={3}
                  borderWidth="1px"
                  borderColor="border.subtle"
                  borderRadius="md"
                  bg="bg.input"
                >
                  <Flex justify="space-between" mb={2}>
                    <Text fontSize="13px" fontWeight="700">
                      AI readiness score
                    </Text>
                    <Text fontSize="13px" fontWeight="700">
                      {scorePercent}% · {result.scoreLabel || 'Score'}
                    </Text>
                  </Flex>
                  <Progress
                    value={scorePercent}
                    size="sm"
                    borderRadius="full"
                    colorScheme={scoreColor(scorePercent)}
                    mb={3}
                  />
                  <Text fontSize="12px" color="text.secondary" mb={2}>
                    Per-document grades from AI (wrong / stock / unreadable files
                    score low even if uploaded).
                  </Text>
                  <VStack align="stretch" spacing={1.5}>
                    {documentScoreRows(result).map((row) => (
                      <Flex key={row.id} justify="space-between" gap={3} align="flex-start">
                        <Box minW={0} flex="1">
                          <Flex align="center" gap={2} mb={0.5}>
                            <Text fontSize="13px" fontWeight="600" noOfLines={1}>
                              {row.label}
                            </Text>
                            {row.status ? (
                              <Badge
                                colorScheme={docStatusColor(row.status)}
                                fontSize="10px"
                                textTransform="uppercase"
                              >
                                {row.status}
                              </Badge>
                            ) : null}
                          </Flex>
                          <Text fontSize="12px" color="text.secondary" noOfLines={3}>
                            {row.detail}
                          </Text>
                        </Box>
                        <Text
                          fontSize="13px"
                          fontWeight="700"
                          flexShrink={0}
                          color={`${scoreColor(row.scorePercent ?? 0)}.600`}
                        >
                          {row.scorePercent != null
                            ? `${row.scorePercent}%`
                            : row.legacy || '—'}
                        </Text>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              ) : null}

              {Array.isArray(result.missingRequired) &&
              result.missingRequired.length ? (
                <Box
                  p={3}
                  borderRadius="md"
                  bg="orange.50"
                  borderWidth="1px"
                  borderColor="orange.200"
                >
                  <Text fontSize="13px" fontWeight="700" mb={1}>
                    Missing compulsory documents
                  </Text>
                  <Text fontSize="13px">
                    {result.missingRequired.join(' · ')}
                  </Text>
                </Box>
              ) : null}

              {Array.isArray(result.complianceAlerts) &&
              result.complianceAlerts.length ? (
                <Box
                  p={3}
                  borderRadius="md"
                  bg="bg.amberTint"
                  borderWidth="1px"
                  borderColor="utkarsh.amber"
                >
                  <Text fontSize="13px" fontWeight="700" mb={1}>
                    Compliance alerts
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {result.complianceAlerts.map((line) => (
                      <Text key={line} fontSize="13px">
                        • {line}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              ) : null}

              <Box>
                <Text fontSize="13px" fontWeight="700" mb={2}>
                  Checkpoints
                </Text>
                <VStack align="stretch" spacing={2}>
                  {(result.checkpoints || []).map((cp) => (
                    <Flex
                      key={cp.id}
                      gap={2}
                      p={2}
                      borderWidth="1px"
                      borderColor="border.subtle"
                      borderRadius="md"
                    >
                      <Badge colorScheme={checkpointColor(cp.status)} h="fit-content">
                        {cp.status}
                      </Badge>
                      <Box minW={0}>
                        <Text fontSize="13px" fontWeight="600">
                          {cp.label || cp.id}
                        </Text>
                        <Text fontSize="12px" color="text.secondary">
                          {cp.detail}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </VStack>
              </Box>

              {Array.isArray(result.mismatches) && result.mismatches.length ? (
                <Box>
                  <Text fontSize="13px" fontWeight="700" mb={2}>
                    Differences
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {result.mismatches.map((m) => (
                      <Text key={m} fontSize="13px">
                        • {m}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              ) : null}

              <Box
                p={3}
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                bg="bg.input"
              >
                <Flex align="center" justify="space-between" gap={2} mb={2} wrap="wrap">
                  <Heading size="xs" mb={0}>
                    Message for owner
                  </Heading>
                  <Text fontSize="11px" color="text.secondary">
                    {userMessage.length}/1000 chars
                  </Text>
                </Flex>
                <Textarea
                  value={userMessage}
                  readOnly
                  rows={8}
                  fontSize="13px"
                  bg="bg.surface"
                  borderColor="border.subtle"
                  _focus={{ borderColor: 'utkarsh.amber' }}
                />
                <Flex gap={2} mt={3} wrap="wrap">
                  <OutlineButton
                    size="sm"
                    leftIcon={<MdContentCopy />}
                    onClick={copyMessage}
                    isDisabled={!userMessage}
                  >
                    Copy message
                  </OutlineButton>
                  <PrimaryButton
                    size="sm"
                    leftIcon={<MdNotificationsActive />}
                    onClick={sendAsNotification}
                    isLoading={sending}
                    isDisabled={!userMessage || !ownerUserId}
                  >
                    Send as notification
                  </PrimaryButton>
                </Flex>
                {!ownerUserId ? (
                  <Text fontSize="12px" color="text.secondary" mt={2}>
                    No linked owner user — copy the message manually.
                  </Text>
                ) : null}
              </Box>

              {!viewingHistory ? (
                <OutlineButton size="sm" onClick={run} isDisabled={loading}>
                  Run again
                </OutlineButton>
              ) : null}
            </VStack>
          ) : null}

          <Box mt={2}>
            <Text fontSize="13px" fontWeight="700" mb={2}>
              Previous runs
            </Text>
            {historyLoading ? (
              <Text fontSize="13px" color="text.secondary">
                Loading history…
              </Text>
            ) : historyError ? (
              <Text fontSize="13px" color="red.400">
                {historyError}
              </Text>
            ) : history.length === 0 ? (
              <Text fontSize="13px" color="text.secondary">
                No previous AI verify runs.
              </Text>
            ) : (
              <VStack align="stretch" spacing={1.5}>
                {history.map((entry) => {
                  const badge = historyStatusBadge(entry);
                  const active = entry.id === selectedHistoryId;
                  return (
                    <Flex
                      key={entry.id}
                      as="button"
                      type="button"
                      textAlign="left"
                      align="center"
                      gap={2}
                      px={3}
                      py={2}
                      borderWidth="1px"
                      borderColor={active ? 'utkarsh.amber' : 'border.subtle'}
                      bg={active ? 'bg.amberTint' : 'bg.input'}
                      borderRadius="md"
                      onClick={() => openHistoryEntry(entry)}
                    >
                      <Badge colorScheme={badge.colorScheme}>{badge.label}</Badge>
                      <Text fontSize="12px" color="text.secondary" flex="1">
                        {formatDateTimeDisplay(entry.ranAt)}
                        {entry.scorePercent != null
                          ? ` · ${entry.scorePercent}%`
                          : ''}
                      </Text>
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <OutlineButton onClick={handleClose}>Close</OutlineButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
